import includes from "lodash/includes";
import { AuthSession } from "./AuthSession";
import { Jwt } from "./Jwt";

export type LoginResponse = {
  accessJwt: Jwt;
  refreshJwt: Jwt;
};

const mfaChallengeApiDtoMethods = ["SMS_MFA", "EMAIL_OTP"] as const;

export type MfaChallengeApiDto = {
  challenge: (typeof mfaChallengeApiDtoMethods)[number];
  userId: string;
  session: string;
  codeDestination?: string | null;
};

export type MfaMethod = "SMS_MFA" | "EMAIL_OTP";

export type MfaChallenge = {
  method: MfaMethod;
  userId: string;
  session: string;
  codeDestination: string | null;
};

export type LoginResult =
  | { type: "authenticated"; session: AuthSession }
  | { type: "mfa-challenge"; challenge: MfaChallenge };

export const isMfaChallengeResponse = <T extends object>(
  body: T | MfaChallengeApiDto
): body is MfaChallengeApiDto => "challenge" in body;

const isMfaChallengeApiDto = (value: unknown): value is MfaChallengeApiDto => {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const { challenge, userId, session, codeDestination } = value as Record<string, unknown>;
  return (
    includes(mfaChallengeApiDtoMethods, challenge) &&
    typeof userId === "string" &&
    typeof session === "string" &&
    (codeDestination == null || typeof codeDestination === "string")
  );
};

export const toMfaChallenge = (dto: MfaChallengeApiDto): MfaChallenge => {
  if (!isMfaChallengeApiDto(dto)) {
    throw new Error("Malformed MFA challenge response");
  }
  return {
    method: dto.challenge,
    userId: dto.userId,
    session: dto.session,
    codeDestination: dto.codeDestination ?? null,
  };
};
